import React, { useState, useEffect, useCallback } from 'react';
import styles from './StateTransitionBanner.module.scss';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlbumState } from '../../../data/models/albumState';
import { FormatType } from '@/data/models/formatType';

type StateTransitionBannerProps = {
  albumId?: string;
  changeRequestId?: string;
  albumTitle?: string;
  newState?: AlbumState;
  returnToState?: boolean;
  formatType?: FormatType;
};

// A stricter type for our internal cache guarantees we have the necessary identifiers
type CachedBannerData = {
  albumId: string;
  changeRequestId?: string;
  albumTitle?: string;
  newState: AlbumState;
  returnToState?: boolean;
  formatType?: FormatType;
};

export default function StateTransitionBanner({
  albumId,
  changeRequestId,
  albumTitle,
  newState,
  returnToState,
  formatType,
}: StateTransitionBannerProps) {
  const { t } = useTranslation();
  const getMessage = useGetStateTransitionMessage();
  const [isVisible, setIsVisible] = useState(false);
  const [bannerData, setBannerData] = useState<CachedBannerData | null>(null);

  // Capture banner props and reset visibility whenever a valid new state transition occurs
  useEffect(() => {
    if (albumId != null && newState != null) {
      setBannerData({
        albumId,
        changeRequestId,
        albumTitle,
        newState,
        returnToState,
        formatType,
      });
      setIsVisible(true);
    }
  }, [albumId, albumTitle, newState, returnToState, formatType]);

  // Automatically hide the banner after 5 seconds
  useEffect(() => {
    if (isVisible) {
      const timeout = setTimeout(() => {
        setIsVisible(false);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [isVisible, bannerData]);

  const displayBanner = isVisible && bannerData != null;

  if (!displayBanner) {
    return null;
  }
  return (
    <div
      key={`${bannerData.albumId}-${bannerData.newState}`}
      className={`${styles['wrapper']} alert alert-success alert-dismissible`}
      role="alert"
    >
      <i className={`icon fas fa-solid fa-check ${styles['icon']}`}></i>
      <span className={styles['text']}>
        {bannerData.changeRequestId != null && (
          <span>{`${t('product.stateTransition.changeRequestFor')} `}</span>
        )}
        {bannerData.albumTitle ? (
          <Link
            to={getProductEditUrl(bannerData, bannerData.formatType)}
            className={styles['link']}
          >
            <strong>{bannerData.albumTitle}</strong>{' '}
          </Link>
        ) : null}

        {getMessage(bannerData.newState, bannerData.returnToState)}
      </span>

      <button
        type="button"
        className="btn-close"
        aria-label="Close"
        onClick={() => setIsVisible(false)}
      />
    </div>
  );
}

function getProductEditUrl(
  bannerData: CachedBannerData,
  formatType?: FormatType
) {
  const albumId = bannerData.albumId;

  if (bannerData.changeRequestId != null) {
    if (bannerData.returnToState) {
      return formatType === FormatType.Merchandise
        ? `/Merchandise/ChangeRequest/${albumId}`
        : `/Physical/ChangeRequest/Album/${albumId}`;
    }

    return formatType === FormatType.Merchandise
      ? `/Merchandise/ProductDetails/${albumId}`
      : `/Physical/ProductDetails/Album/${albumId}`;
  }

  if (bannerData.newState === AlbumState.Released) {
    return formatType === FormatType.Merchandise
      ? `/Merchandise/ProductDetails/${albumId}`
      : `/Physical/ProductDetails/Album/${albumId}`;
  }

  return formatType === FormatType.Merchandise
    ? `/Merchandise/EditProduct/${albumId}`
    : `/Physical/EditProduct/Album/${albumId}`;
}

function useGetStateTransitionMessage() {
  const { t } = useTranslation();
  return useCallback(
    (newState: AlbumState, returnToState?: boolean) => {
      switch (newState) {
        case AlbumState.PendingImport: {
          return returnToState
            ? t('product.stateTransition.returnToPendingImport')
            : t('product.stateTransition.submitForImport');
        }
        case AlbumState.Draft: {
          return returnToState
            ? t('product.stateTransition.returnToDraft')
            : t('product.stateTransition.draft');
        }
        case AlbumState.UnderReview: {
          return returnToState
            ? t('product.stateTransition.returnToUnderReview')
            : t('product.stateTransition.submitForReview');
        }
        case AlbumState.Rejected: {
          return t('product.stateTransition.reject');
        }
        case AlbumState.Released: {
          return t('product.stateTransition.release');
        }
        case AlbumState.Error: {
          return t('product.stateTransition.error');
        }
        default:
          return '';
      }
    },
    [t]
  );
}
